import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, ApiError, getOrStartRefresh, registerAuthHandlers } from "@/lib/api";
import { clearNavCache } from "@/modules/module-manager/hooks/useModules";
import * as Sentry from "@sentry/react";

export type AuthRole = {
  id: string;
  name: string;
  isSystem: boolean;
};

export type ModulePermission = {
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canExport: boolean;
  canAdmin: boolean;
};

export type AuthUser = {
  id: string;
  email: string;
  name: string;
  mustChangePassword: boolean;
  mfaEnabled: boolean;
  roles: AuthRole[];
  permissions: Record<string, ModulePermission>;
};

/** Password accepted; a second factor is needed to finish signing in. */
export type MfaChallenge = {
  mfaRequired: true;
  mfaToken: string;
};

export type AuthContextValue = {
  user: AuthUser | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Resolves to an MfaChallenge when the account has 2FA, else null (signed in). */
  login: (email: string, password: string) => Promise<MfaChallenge | null>;
  verifyMfa: (mfaToken: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
};

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const SESSION_HINT_KEY = "phazex.hasSession";
// Cold-start dyno wakes can exceed 30s; keep the bootstrap forgiving so a slow
// first request does not auto-signout the user.
const BOOTSTRAP_TIMEOUT_MS = 45000;

type LoginResponse = {
  user: AuthUser;
  accessToken: string;
};

type MeResponse = {
  user: AuthUser;
};

function readSessionHint(): boolean {
  try {
    return typeof window !== "undefined" && window.localStorage.getItem(SESSION_HINT_KEY) === "1";
  } catch {
    return false;
  }
}

function setSessionHint(value: boolean) {
  try {
    if (typeof window === "undefined") return;
    if (value) window.localStorage.setItem(SESSION_HINT_KEY, "1");
    else window.localStorage.removeItem(SESSION_HINT_KEY);
  } catch {
    // ignore storage errors
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  const startSession = useCallback(
    (res: LoginResponse) => {
      setAccessToken(res.accessToken);
      setUser(res.user);
      Sentry.setUser({ id: res.user.id, email: res.user.email });
      setSessionHint(true);
      navigate("/dashboard", { replace: true });
    },
    [navigate],
  );

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await apiFetch<LoginResponse | MfaChallenge>("/auth/login", {
        method: "POST",
        body: { email, password },
      });
      if ("mfaRequired" in res) return res;
      startSession(res);
      return null;
    },
    [startSession],
  );

  const verifyMfa = useCallback(
    async (mfaToken: string, code: string) => {
      const res = await apiFetch<LoginResponse>("/auth/login/mfa", {
        method: "POST",
        body: { mfaToken, code },
      });
      startSession(res);
    },
    [startSession],
  );

  const refreshUser = useCallback(async () => {
    if (!accessToken) return;
    try {
      const me = await apiFetch<MeResponse>("/auth/me", { token: accessToken });
      setUser(me.user);
    } catch (err) {
      console.error("[AuthProvider] refreshUser failed:", err);
    }
  }, [accessToken]);

  const logout = useCallback(async () => {
    try {
      if (accessToken) {
        await apiFetch<{ success: true }>("/auth/logout", {
          method: "POST",
          token: accessToken,
        });
      }
    } catch {
      // ignore — always clear local state
    }
    setAccessToken(null);
    setUser(null);
    Sentry.setUser(null);
    setSessionHint(false);
    clearNavCache();
    localStorage.removeItem('phazex.sidebar.expandedSections');
    navigate("/login", { replace: true });
  }, [accessToken, navigate]);

  useEffect(() => {
    registerAuthHandlers({
      onRefresh: (token) => setAccessToken(token),
      onAuthFailure: () => {
        setAccessToken(null);
        setUser(null);
        setSessionHint(false);
        clearNavCache();
      },
    });
    return () => registerAuthHandlers({});
  }, []);

  useEffect(() => {
    if (!readSessionHint()) {
      setAccessToken(null);
      setUser(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => {
      controller.abort();
    }, BOOTSTRAP_TIMEOUT_MS);

    (async () => {
      try {
        // Route through the shared singleton so bootstrap shares the same
        // in-flight refresh as any other 401 fired during initial render.
        const accessToken = await getOrStartRefresh();
        if (cancelled) return;
        const me = await apiFetch<MeResponse>("/auth/me", {
          token: accessToken,
          signal: controller.signal,
        });
        if (cancelled) return;
        setAccessToken(accessToken);
        setUser(me.user);
        Sentry.setUser({ id: me.user.id, email: me.user.email });
      } catch (err) {
        if (cancelled) return;
        const isAbort =
          (err instanceof DOMException && err.name === "AbortError") ||
          (err instanceof Error && err.name === "AbortError");
        const isAuthExpired =
          err instanceof ApiError && (err.status === 401 || err.status === 403);
        if (!isAbort && !(err instanceof ApiError)) {
          console.error("[AuthProvider] bootstrap failed:", err);
        }
        setAccessToken(null);
        setUser(null);
        // Only clear the session hint when the server confirms the refresh
        // token is dead. Aborts and transient failures may recover on reload.
        if (isAuthExpired) setSessionHint(false);
        // No explicit navigate — ProtectedRoute will redirect to /login on its
        // own when isAuthenticated becomes false.
      } finally {
        window.clearTimeout(timeoutId);
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [navigate]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      accessToken,
      isAuthenticated: Boolean(user && accessToken),
      isLoading,
      login,
      verifyMfa,
      logout,
      refreshUser,
    }),
    [user, accessToken, isLoading, login, verifyMfa, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
